import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { B2BUnitOfflineHandler } from '@asm/features/sync/interceptor/handlers/b2b-unit.handler';
import { CartOfflineHandler } from '@asm/features/sync/interceptor/handlers/cart.handler';
import { PromotionOfflineHandler } from '@asm/features/sync/interceptor/handlers/promotion.handler';
import { CatalogOfflineHandler } from '@asm/features/sync/interceptor/handlers/catalog.handler';
import { FinancialTitleOfflineHandler } from '@asm/features/sync/interceptor/handlers/financial-title.handler';
import { MixOfflineHandler } from '@asm/features/sync/interceptor/handlers/mix.handler';
import { OrderOfflineHandler } from '@asm/features/sync/interceptor/handlers/order.handler';
import { PaymentOfflineHandler } from '@asm/features/sync/interceptor/handlers/payment.handler';
import { PricingOfflineHandler } from '@asm/features/sync/interceptor/handlers/pricing.handler';
import { ProductOfflineHandler } from '@asm/features/sync/interceptor/handlers/product.handler';
import { StoreConfigOfflineHandler } from '@asm/features/sync/interceptor/handlers/store-config.handler';
import { StockOfflineHandler } from '@asm/features/sync/interceptor/handlers/stock.handler';
import { WarehouseOfflineHandler } from '@asm/features/sync/interceptor/handlers/warehouse.handler';
import { QuotaOfflineHandler } from '@asm/features/sync/interceptor/handlers/quota.handler';
import { BillingClientsOfflineHandler } from '@asm/features/sync/interceptor/handlers/billing-clients.handler';
import { AppModeService } from '../shared/app-mode/app-mode.service';

/**
 * Interceptor HTTP para modo offline.
 *
 * Quando o dispositivo está sem rede ou o usuário ativou o modo offline,
 * cada request é delegado ao handler de domínio correspondente.
 *
 * Arquitetura de handlers:
 * - B2BUnitOfflineHandler        → GET /b2b-unit/me
 * - CatalogOfflineHandler        → GET /category/select
 * - WarehouseOfflineHandler      → GET /warehouse/select, /delivery-routes/available-days
 * - PaymentOfflineHandler        → GET /payment-method/select, /payment-condition/select, /bank/select
 * - OrderConfigOfflineHandler    → GET /document-type/*, /freight-type/select, /storage-condition/select, /country-origin/select
 * - FinancialTitleOfflineHandler → GET /financial-title/has-overdue
 * - OrderReferenceOfflineHandler → GET /order/reference-select
 * - StoreConfigOfflineHandler    → GET /dashboard/dashboard-config, /store/config
 * - MixOfflineHandler            → mixes paginados e seleção
 * - ProductOfflineHandler        → busca de produtos e similares
 * - PricingOfflineHandler        → cálculo de preço local
 * - StockOfflineHandler          → estoque e alocação
 * - PromotionOfflineHandler      → GET /cart/coupon/available, POST|DELETE /cart/:code/coupon
 * - CartOfflineHandler           → operações de carrinho
 * - OrderOfflineHandler          → place-order e mix-adherence
 * - QuotaOfflineHandler          → GET /quota (listagem de cotas)
 * - BillingClientsOfflineHandler → GET /b2b-unit/billing-clients
 */
@Injectable()
export class OfflineInterceptor implements HttpInterceptor {
  private readonly appMode = inject(AppModeService);

  private readonly handlers = [
    inject(B2BUnitOfflineHandler),
    inject(CatalogOfflineHandler),
    inject(WarehouseOfflineHandler),
    inject(PaymentOfflineHandler),
    inject(FinancialTitleOfflineHandler),
    inject(StoreConfigOfflineHandler),
    inject(MixOfflineHandler),
    inject(ProductOfflineHandler),
    inject(PricingOfflineHandler),
    inject(StockOfflineHandler),
    inject(PromotionOfflineHandler),
    inject(CartOfflineHandler),
    inject(OrderOfflineHandler),
    inject(QuotaOfflineHandler),
    inject(BillingClientsOfflineHandler),
  ];

  intercept(req: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isLocalAssetRequest(req.url)) {
      return handler.handle(req);
    }

    const isNetworkOffline = !navigator.onLine;
    const isModeOffline = this.appMode.isOfflineMode();

    if (req.url.includes('/genericapi/inbound/data')) {
      if (isNetworkOffline || isModeOffline) {
        return throwError(() => new Error('Sincronizacao requer conexao com a internet.'));
      }
      return handler.handle(req);
    }

    if (isNetworkOffline || isModeOffline) {
      return from(this.dispatch(req));
    }

    return handler.handle(req);
  }

  private async dispatch(req: HttpRequest<unknown>): Promise<HttpEvent<unknown>> {
    for (const offlineHandler of this.handlers) {
      if (offlineHandler.canHandle(req)) {
        return offlineHandler.handle(req);
      }
    }

    throw new Error(`[OfflineInterceptor] Rota nao mapeada para modo offline: ${req.method} ${req.url}`);
  }

  private isLocalAssetRequest(url: string): boolean {
    return url.startsWith('assets/') || url.includes('/assets/');
  }
}
